import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Divider,
} from '@mui/material';
import { useUpdateCompanyEmailTemplates } from '@/lib/query/hooks/useCompanies';
import type { CompanyEmailTemplates, UpdateEmailTemplatesRequest } from '@/lib/types/mise/company';

interface EmailTemplateEditDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  templates: CompanyEmailTemplates;
}

export function EmailTemplateEditDialog({
  open,
  onClose,
  companyId,
  templates,
}: EmailTemplateEditDialogProps) {
  const [invitationSubject, setInvitationSubject] = useState('');
  const [invitationBody, setInvitationBody] = useState('');
  const [verificationSubject, setVerificationSubject] = useState('');
  const [verificationBody, setVerificationBody] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateMutation = useUpdateCompanyEmailTemplates(companyId);

  // 初期値の設定
  useEffect(() => {
    if (templates && open) {
      setInvitationSubject(templates.invitation.subject);
      setInvitationBody(templates.invitation.body);
      setVerificationSubject(templates.verification.subject);
      setVerificationBody(templates.verification.body);
      setErrors({});
      updateMutation.reset();
    }
  }, [templates, open]);

  // バリデーション
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!invitationSubject.trim()) {
      newErrors.invitationSubject = '招待メールの件名は必須です';
    }
    if (!invitationBody.trim()) {
      newErrors.invitationBody = '招待メールの本文は必須です';
    }
    if (!verificationSubject.trim()) {
      newErrors.verificationSubject = '検証メールの件名は必須です';
    }
    if (!verificationBody.trim()) {
      newErrors.verificationBody = '検証メールの本文は必須です';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    if (updateMutation.isPending) return;
    setConfirmDialogOpen(false);
    onClose();
  };

  const handleSaveClick = () => {
    if (!validate()) return;
    setConfirmDialogOpen(true);
  };

  const handleConfirmSave = async () => {
    if (!validate()) {
      setConfirmDialogOpen(false);
      return;
    }

    const request: UpdateEmailTemplatesRequest = {
      invitation: {
        subject: invitationSubject,
        body: invitationBody,
      },
      verification: {
        subject: verificationSubject,
        body: verificationBody,
      },
    };

    try {
      await updateMutation.mutateAsync(request);
      handleClose();
    } catch (error) {
      console.error('メールテンプレート更新に失敗しました:', error);
      setConfirmDialogOpen(false);
    }
  };

  return (
    <>
      {/* 編集ダイアログ */}
      <Dialog open={open && !confirmDialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>メールテンプレートを編集</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {updateMutation.isError && (
              <Alert severity="error">
                {updateMutation.error instanceof Error
                  ? updateMutation.error.message
                  : 'メールテンプレートの更新に失敗しました。'}
              </Alert>
            )}

            {/* プレースホルダー説明 */}
            <Alert severity="info">
              <Typography variant="body2">
                <strong>プレースホルダーについて:</strong>
              </Typography>
              <Typography variant="body2">
                {'・{username} - ユーザーのメールアドレスに置換されます（招待メールのみ）'}
              </Typography>
              <Typography variant="body2">
                {
                  '・{####} - 仮パスワード（招待メール）または確認コード（検証メール）に置換されます'
                }
              </Typography>
            </Alert>

            {/* 招待メッセージ */}
            <Typography variant="subtitle1" fontWeight="bold">
              招待メッセージ（新規ユーザー登録時）
            </Typography>
            <TextField
              label="件名"
              value={invitationSubject}
              onChange={(e) => {
                setInvitationSubject(e.target.value);
                if (errors.invitationSubject) setErrors({ ...errors, invitationSubject: '' });
              }}
              error={!!errors.invitationSubject}
              helperText={errors.invitationSubject}
              fullWidth
              required
              disabled={updateMutation.isPending}
            />
            <TextField
              label="本文（HTML形式）"
              value={invitationBody}
              onChange={(e) => {
                setInvitationBody(e.target.value);
                if (errors.invitationBody) setErrors({ ...errors, invitationBody: '' });
              }}
              error={!!errors.invitationBody}
              helperText={errors.invitationBody || 'HTML形式で記述してください（例: <br/>で改行）'}
              fullWidth
              required
              multiline
              minRows={6}
              maxRows={12}
              disabled={updateMutation.isPending}
            />

            <Divider />

            {/* 検証メッセージ */}
            <Typography variant="subtitle1" fontWeight="bold">
              検証メッセージ（パスワードリセット時）
            </Typography>
            <TextField
              label="件名"
              value={verificationSubject}
              onChange={(e) => {
                setVerificationSubject(e.target.value);
                if (errors.verificationSubject) setErrors({ ...errors, verificationSubject: '' });
              }}
              error={!!errors.verificationSubject}
              helperText={errors.verificationSubject}
              fullWidth
              required
              disabled={updateMutation.isPending}
            />
            <TextField
              label="本文"
              value={verificationBody}
              onChange={(e) => {
                setVerificationBody(e.target.value);
                if (errors.verificationBody) setErrors({ ...errors, verificationBody: '' });
              }}
              error={!!errors.verificationBody}
              helperText={errors.verificationBody}
              fullWidth
              required
              multiline
              minRows={3}
              maxRows={6}
              disabled={updateMutation.isPending}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={updateMutation.isPending}>
            キャンセル
          </Button>
          <Button onClick={handleSaveClick} variant="contained" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 確認ダイアログ */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-container': {
            zIndex: 1400,
          },
        }}
      >
        <DialogTitle>メールテンプレートを更新しますか？</DialogTitle>
        <DialogContent>
          <Typography>
            この操作により、Cognitoのメールテンプレートが更新されます。更新後に送信されるメールから新しいテンプレートが適用されます。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={updateMutation.isPending}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmSave}
            variant="contained"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? '更新中...' : '更新する'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
